import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { useAcList } from "../api/accessControlApi";
import { parkingApi, usePkList, usePkMutation, type PkLot, type SpaceType } from "../api/parkingApi";
import { ConsolePage, LoadState, Panel, StatusChip, ToolButton } from "../components/console/Console";
import FormModal, { type FormValues } from "../components/console/FormModal";
import { LOT_ICON, errorText } from "./parkingUi";
import { SELF_INVOICE_PATH } from "./parkingPdf";

// ===========================================================================
// Control de estacionamientos → Configuración: estacionamientos (tipo, niveles,
// sitio y plumas de entrada/salida de Nuvex Access, tarifa, tiempo para salir,
// foto), alta de cajones por nivel, portal de autofactura y estado del PAC.
// ===========================================================================

const LOT_TYPES = ["covered", "open", "multilevel", "underground"] as const;
const SPACE_TYPES: SpaceType[] = ["standard", "compact", "disabled", "ev", "motorcycle", "vip"];

export default function SettingsScreen() {
  const { t } = useTranslation("parking");
  const lotsQ = usePkList("lots");
  const ratesQ = usePkList("rates");
  const spacesQ = usePkList("spaces");
  const sitesQ = useAcList("sites");
  const pointsQ = useAcList("points");
  const pacQ = useQuery({ queryKey: ["parking", "pac"], queryFn: parkingApi.pacStatus, staleTime: 300_000 });
  const [edit, setEdit] = useState<Partial<PkLot> | null>(null);
  const [spacesFor, setSpacesFor] = useState<PkLot | null>(null);
  const [progress, setProgress] = useState<string | null>(null);

  const save = usePkMutation(({ id, body }: { id?: string; body: Partial<PkLot> }) => (id ? parkingApi.update("lots", id, body) : parkingApi.create("lots", body)));
  const barriers = (pointsQ.data ?? []).filter((p) => ["barrier", "gate"].includes(p.type));
  const portal = `${window.location.origin}${SELF_INVOICE_PATH}`;

  const submit = async (v: FormValues) => {
    const levels = String(v.levels || "PB:Planta baja")
      .split(/\n|;/)
      .map((x) => x.trim())
      .filter(Boolean)
      .map((x) => {
        const [id, ...name] = x.split(":");
        return { id: id.trim().toUpperCase(), name: (name.join(":") || id).trim() };
      });
    try {
      await save.mutateAsync({ id: edit?.id, body: { ...(v as unknown as Partial<PkLot>), levels, exit_grace_minutes: Number(v.exit_grace_minutes || 15) } });
      toast.success(t("common.saved"));
      setEdit(null);
    } catch (err) {
      toast.error(errorText(err, t("common.saveError")));
    }
  };

  const createSpaces = async (v: FormValues) => {
    if (!spacesFor) return;
    const level = String(v.level);
    const from = Number(v.from || 1);
    const to = Math.min(Number(v.to || from), from + 99);
    const existing = new Set((spacesQ.data ?? []).filter((s) => s.lot_id === spacesFor.id).map((s) => s.code));
    let made = 0;
    for (let n = from; n <= to; n += 1) {
      const code = `${level}-${String(n).padStart(2, "0")}`;
      if (existing.has(code)) continue;
      setProgress(t("settings.creatingSpaces", { interpolation: { escapeValue: false }, done: made, total: to - from + 1 }));
      try {
        await parkingApi.create("spaces", { lot_id: spacesFor.id, level, code, type: String(v.type) as SpaceType });
        made += 1;
      } catch (err) {
        toast.error(errorText(err, t("common.saveError")));
        break;
      }
    }
    setProgress(null);
    await spacesQ.refetch();
    toast.success(t("settings.spacesCreated", { interpolation: { escapeValue: false }, count: made }));
    setSpacesFor(null);
  };

  return (
    <ConsolePage icon="ri-settings-3-line" title={t("settings.title")} subtitle={t("settings.subtitle")} actions={<ToolButton icon="ri-add-line" variant="primary" onClick={() => setEdit({ type: "open", status: "active", exit_grace_minutes: 15, opening_hours: "24/7", levels: [{ id: "PB", name: "Planta baja" }], entry_point_ids: [], exit_point_ids: [] })}>{t("settings.newLot")}</ToolButton>}>
      <LoadState loading={lotsQ.isLoading} error={lotsQ.error} empty={!lotsQ.data?.length} emptyText={t("settings.noLots")} onRetry={() => lotsQ.refetch()}>
        <div className="grid gap-4 lg:grid-cols-2">
          {(lotsQ.data ?? []).map((l) => {
            const mine = (spacesQ.data ?? []).filter((s) => s.lot_id === l.id);
            const rate = (ratesQ.data ?? []).find((r) => r.id === l.rate_id);
            const pointName = (id: string) => (pointsQ.data ?? []).find((p) => p.id === id)?.name ?? id;
            return (
              <article key={l.id} className="overflow-hidden rounded-xl border border-line bg-card">
                {l.photo_url && <img src={l.photo_url} alt="" className="aspect-[21/9] w-full object-cover" loading="lazy" />}
                <div className="p-4">
                  <header className="mb-2 flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="truncate font-semibold text-content"><i className={LOT_ICON[l.type]} /> {l.name}</h3>
                      <p className="truncate text-xs text-content-secondary">{l.address} · {l.city}</p>
                    </div>
                    <StatusChip tone={l.status === "active" ? "success" : "warning"}>{t(`lotStatus.${l.status}`)}</StatusChip>
                  </header>
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <dt className="text-content-secondary">{t("settings.code")}</dt><dd className="text-right font-mono text-content">{l.code}</dd>
                    <dt className="text-content-secondary">{t("settings.type")}</dt><dd className="text-right text-content">{t(`lotType.${l.type}`)}</dd>
                    <dt className="text-content-secondary">{t("settings.levels")}</dt><dd className="text-right text-content">{l.levels.map((x) => x.id).join(" · ")}</dd>
                    <dt className="text-content-secondary">{t("settings.spaces")}</dt><dd className="text-right tabular-nums text-content">{mine.length}</dd>
                    <dt className="text-content-secondary">{t("settings.site")}</dt><dd className="truncate text-right text-content">{l.site_name ?? "—"}</dd>
                    <dt className="text-content-secondary">{t("settings.entryPoints")}</dt><dd className="truncate text-right text-content">{(l.entry_point_ids ?? []).map(pointName).join(", ") || "—"}</dd>
                    <dt className="text-content-secondary">{t("settings.exitPoints")}</dt><dd className="truncate text-right text-content">{(l.exit_point_ids ?? []).map(pointName).join(", ") || "—"}</dd>
                    <dt className="text-content-secondary">{t("settings.rate")}</dt><dd className="truncate text-right text-content">{rate?.name ?? "—"}</dd>
                    <dt className="text-content-secondary">{t("settings.exitGrace")}</dt><dd className="text-right text-content">{l.exit_grace_minutes ?? 15} min</dd>
                    <dt className="text-content-secondary">{t("settings.hours")}</dt><dd className="text-right text-content">{l.opening_hours}</dd>
                  </dl>
                  <footer className="mt-3 flex flex-wrap justify-end gap-2">
                    <ToolButton icon="ri-layout-grid-line" onClick={() => setSpacesFor(l)}>{t("settings.addSpaces")}</ToolButton>
                    <ToolButton icon="ri-edit-line" onClick={() => setEdit(l)}>{t("common.edit")}</ToolButton>
                  </footer>
                </div>
              </article>
            );
          })}
        </div>
      </LoadState>

      <div className="grid gap-4 md:grid-cols-2">
        <Panel title={t("settings.portal")} icon="ri-global-line">
          <p className="mb-2 text-sm text-content-secondary">{t("settings.portalHint")}</p>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-md bg-subtle px-2 py-1 text-xs text-content">{portal}</code>
            <ToolButton icon="ri-file-copy-line" onClick={() => navigator.clipboard?.writeText(portal).then(() => toast.success(t("common.copied")))}>{t("common.copyLink")}</ToolButton>
          </div>
        </Panel>
        <Panel title={t("settings.pac")} icon="ri-quill-pen-line">
          <p className="text-sm text-content-secondary">{pacQ.data?.enabled ? t("invoices.pacOn", { interpolation: { escapeValue: false }, provider: pacQ.data.provider }) : t("settings.pacHint")}</p>
        </Panel>
      </div>

      {edit && (
        <FormModal
          title={edit.id ? t("settings.editLot") : t("settings.newLot")}
          submitLabel={t("common.save")}
          busy={save.isPending}
          onClose={() => setEdit(null)}
          onSubmit={submit}
          initial={{ ...(edit as unknown as FormValues), levels: (edit.levels ?? []).map((x) => `${x.id}:${x.name}`).join("\n") }}
          fields={[
            { key: "name", label: t("settings.name"), required: true, full: true },
            { key: "type", label: t("settings.type"), type: "select", required: true, options: LOT_TYPES.map((x) => ({ value: x, label: t(`lotType.${x}`) })) },
            { key: "status", label: t("common.status"), type: "select", required: true, options: (["active", "maintenance", "inactive"] as const).map((x) => ({ value: x, label: t(`lotStatus.${x}`) })) },
            { key: "address", label: t("settings.address") },
            { key: "city", label: t("settings.city") },
            { key: "site_id", label: t("settings.site"), type: "select", options: (sitesQ.data ?? []).map((s) => ({ value: s.id, label: s.name })) },
            { key: "rate_id", label: t("settings.rate"), type: "select", options: (ratesQ.data ?? []).map((r) => ({ value: r.id, label: r.name })) },
            { key: "entry_point_ids", label: t("settings.entryPoints"), type: "multiselect", options: barriers.map((p) => ({ value: p.id, label: p.name })), hint: t("settings.pointsHint") },
            { key: "exit_point_ids", label: t("settings.exitPoints"), type: "multiselect", options: barriers.map((p) => ({ value: p.id, label: p.name })) },
            { key: "levels", label: t("settings.levels"), type: "textarea", hint: t("settings.levelsHint") },
            { key: "exit_grace_minutes", label: t("settings.exitGrace"), type: "number", min: 0, max: 240 },
            { key: "opening_hours", label: t("settings.hours") },
            { key: "photo_url", label: t("settings.photo"), placeholder: "https://…", hint: t("settings.photoHint"), full: true },
          ]}
        />
      )}
      {spacesFor && (
        <FormModal
          title={t("settings.addSpacesTitle", { interpolation: { escapeValue: false }, name: spacesFor.name })}
          submitLabel={progress ?? t("settings.addSpaces")}
          busy={Boolean(progress)}
          onClose={() => setSpacesFor(null)}
          onSubmit={createSpaces}
          initial={{ level: spacesFor.levels[0]?.id ?? "PB", from: 1, to: 10, type: "standard" }}
          fields={[
            { key: "level", label: t("occupancy.level"), type: "select", required: true, options: spacesFor.levels.map((x) => ({ value: x.id, label: `${x.id} · ${x.name}` })) },
            { key: "type", label: t("occupancy.type"), type: "select", required: true, options: SPACE_TYPES.map((x) => ({ value: x, label: t(`spaceType.${x}`) })) },
            { key: "from", label: t("settings.from"), type: "number", min: 1, required: true },
            { key: "to", label: t("settings.to"), type: "number", min: 1, required: true, hint: t("settings.rangeHint") },
          ]}
        />
      )}
    </ConsolePage>
  );
}
